// components/promoCode/AddPromoCodeModal.tsx
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { FaUser } from "react-icons/fa";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useAddPromo } from "../../apis/promo-codes/add-promo";
import { isPromoCodeDescriptionValid } from "../../utils/helpers";
import { serviceTypeMap } from "../../pages/ServicesDashboard";
import Button from "../Button";
import Checkbox from "../Checkbox";
import DatePicker from "../DatePicker";
import Input from "../Input";
import Modal from "../Modal";
import Select from "../Select";
import Textarea from "../Textarea";
import Toggle from "../Toggle";
import { FormValues } from "./EditPromoCode";

interface PropTypes {
  isVisible: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

export const getTodayDate = () => new Date().toISOString().split("T")[0];

export const serviceTypes = [
  { label: "Package pickup", value: "package_pickup_meta" },
  { label: "Curbside pickup", value: "curbside_pickup_meta" },
  { label: "Instore pickup", value: "instore_pickup_meta" },
];

export const discountOptions = [
  { label: "Flat discount", value: "flat" },
  { label: "Percentage discount", value: "percentage" },
];

const AddPromoCodeModal = ({ isVisible, onHide, onSuccess }: PropTypes) => {
  const [adminAddedDescription, setAdminAddedDescription] = useState(false);
  const { mutateAsync: addPromo } = useAddPromo();
  const isDesktop = window.innerWidth > 850;

  const handleHide = () => {
    setAdminAddedDescription(false);
    onHide();
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      promoCode: "",
      serviceType: [],
      discountType: "",
      discountValue: "",
      description: "",
      startDate: "",
      expiryDate: null,
      maxUsers: "",
      maxBudget: "",
      isNotifyUser: "false",
      isPromoCodeActive: true,
      isSingleUse: "false",
    },
    validationSchema: Yup.object().shape({
      promoCode: Yup.string().trim().matches(/^\S*$/, "Promo code cannot contain spaces").required("This field is required"),
      serviceType: Yup.array().min(1, "At least one service is required").required("This field is required"),
      discountType: Yup.string().required("This field is required"),
      discountValue: Yup.number().positive().required("This field is required"),
      description: Yup.string().trim().required("This field is required").test(
        "description-match",
        "Description doesn't match selected options",
        function (value) {
          const { discountType, discountValue, serviceType } = this.parent;
          const validation = isPromoCodeDescriptionValid(value, discountType, discountValue, serviceType, serviceTypeMap);
          if (!validation.isValid) return this.createError({ message: validation.error });
          return true;
        }
      ),
      startDate: Yup.date().min(getTodayDate(), "Start Date must be today or future date").required("This field is required"),
      maxUsers: Yup.number().positive(),
      maxBudget: Yup.number().positive(),
      expiryDate: Yup.date()
        .min(Yup.ref("startDate"), "Expiry date must be after the start date")
        .test("is-greater", "Expiry date must be after the start date", function (value) {
          const { startDate } = this.parent;
          if (!value) return true;
          return new Date(value) > new Date(startDate);
        })
        .nullable(),
      isNotifyUser: Yup.string().required("This field is required"),
      isSingleUse: Yup.string().required("This field is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { serviceType, maxBudget, isNotifyUser, maxUsers, discountValue, isSingleUse, ...rest } = values;
      try {
        await addPromo({
          ...rest,
          serviceType: serviceType.includes("all") ? serviceTypes.map((s) => s.value) : serviceType,
          maxUsers: parseInt(maxUsers),
          maxBudget: parseInt(maxBudget),
          discountValue: parseInt(discountValue),
          isNotifyUser: isNotifyUser === "true",
          isSingleUse: isSingleUse === "true",
          id: "",
        });
        toast.success("Promo Code Added Successfully");
        resetForm();
        onSuccess();
        handleHide();
      } catch (error: any) {
        toast.error(error?.response?.data?.error || "Something went wrong");
      }
    },
  });

  const handleServiceTypeChange = useCallback(
    (service: { label: string; value: string }, checked: boolean) => {
      let current = formik.values.serviceType.includes("all") ? [] : [...formik.values.serviceType];
      formik.setFieldValue("serviceType", checked
        ? [...current, service.value]
        : current.filter((item) => item !== service.value)
      );
    },
    [formik.values.serviceType, formik.setFieldValue]
  );

  useEffect(() => {
    if (formik.values.discountType && formik.values.discountValue && !adminAddedDescription) {
      formik.setFieldValue(
        "description",
        `Enjoy ${formik.values.discountType === "flat" ? "$" : ""} ${formik.values.discountValue} ${formik.values.discountType === "percentage" ? "%" : ""} discount on this offer for ${formik.values.serviceType.map((service) => `${serviceTypeMap[service]} Pickup`)}`
      );
    }
  }, [formik.values.discountType, formik.values.discountValue, formik.values.serviceType, adminAddedDescription]);

  const ExpiryDatePicker = () => (
    <DatePicker
      name="expiryDate"
      label="Valid till (optional)"
      placeholderText="MM/DD/YYYY"
      className="text-[1rem] lg:text-xs"
      inputClassName="text-[1rem] lg:text-xs"
      selected={formik.values.expiryDate ? new Date(formik.values.expiryDate) : null}
      onChange={(date: Date | null) => formik.setFieldValue("expiryDate", date ? date.toISOString() : null)}
      onBlur={() => formik.setFieldTouched("expiryDate", true)}
      isInvalid={Boolean(formik.touched.expiryDate && formik.errors.expiryDate)}
      error={formik.errors.expiryDate}
      dateFormat="MM/dd/yyyy"
      popperPlacement="top"
      wrapperClassName="w-full mt-1 md:mt-0"
    />
  );

  return (
    <Modal isVisible={isVisible} onHide={() => { formik.resetForm(); handleHide(); }} heading="Add promo code">
      <form onSubmit={formik.handleSubmit} className="px-8">
        <div className="flex flex-col md:grid md:grid-cols-[70%_30%] gap-x-4">
          <div className="flex flex-col md:grid md:grid-col-2 gap-x-4">
            <Input
              placeholder="Enter promo code"
              isInvalid={Boolean(formik.touched.promoCode && formik.errors.promoCode)}
              className="input-primary"
              label="Promo code *"
              name="promoCode"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.promoCode}
              value={formik.values.promoCode}
            />
            <DatePicker
              name="startDate"
              label="Valid from *"
              placeholderText="MM/DD/YYYY"
              className="text-[1rem] lg:text-xs"
              inputClassName="text-[1rem] lg:text-xs"
              selected={formik.values.startDate ? new Date(formik.values.startDate) : null}
              onChange={(date) => formik.setFieldValue("startDate", date ? date.toISOString() : null)}
              onBlur={formik.handleBlur}
              error={formik.errors.startDate}
              isInvalid={Boolean(formik.touched.startDate && formik.errors.startDate)}
              dateFormat="MM/dd/yyyy"
              popperPlacement="top"
              wrapperClassName="w-full"
            />
            {!isDesktop && <ExpiryDatePicker />}
            <Select
              placeholder="Select a Discount Type"
              isInvalid={Boolean(formik.touched.discountType && formik.errors.discountType)}
              className="select-primary text-[1rem] lg:text-xs"
              containerClassName="mb-0"
              label="Discount type *"
              name="discountType"
              value={formik.values.discountType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.discountType}
              options={discountOptions}
            />
            <Input
              containerClassName="mb-1"
              placeholder="Enter value"
              rightContent={
                <div className="flex-shrink-0 border-l border-primary flex h-full items-center justify-center bg-gray-200 w-8 rounded-r-lg text-black">
                  {formik.values.discountType === "flat" ? "$" : formik.values.discountType === "percentage" ? "%" : "$"}
                </div>
              }
              isInvalid={Boolean(formik.touched.discountValue && formik.errors.discountValue)}
              label="Of *"
              className="input-primary"
              name="discountValue"
              value={formik.values.discountValue}
              type="number"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.discountValue}
            />
            <Input
              rightContent={<div className="flex-shrink-0 border-l border-primary flex h-full items-center justify-center bg-gray-200 w-8 rounded-r-lg text-black"><FaUser /></div>}
              placeholder="Enter value"
              type="number"
              isInvalid={Boolean(formik.touched.maxUsers && formik.errors.maxUsers)}
              className="input-primary"
              label="Max users"
              name="maxUsers"
              onChange={formik.handleChange}
              value={formik.values.maxUsers}
              onBlur={formik.handleBlur}
              error={formik.errors.maxUsers}
            />
            <Input
              rightContent={<div className="flex-shrink-0 border-l border-primary flex h-full items-center justify-center bg-gray-200 w-8 rounded-r-lg text-black">$</div>}
              placeholder="Enter value"
              type="number"
              className="input-primary"
              isInvalid={Boolean(formik.touched.maxBudget && formik.errors.maxBudget)}
              label="Max budget"
              name="maxBudget"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.maxBudget}
              value={formik.values.maxBudget}
            />
            <div className="col-start-1 col-end-3">
              <Textarea
                placeholder="Test discount"
                className="textarea-primary text-[1rem] lg:text-xs h-20"
                isInvalid={Boolean(formik.touched.description && formik.errors.description)}
                label="Discount description *"
                name="description"
                onChange={(e) => {
                  formik.setFieldValue("description", e.target.value);
                  setAdminAddedDescription(e.target.value !== "");
                }}
                onBlur={formik.handleBlur}
                error={formik.errors.description}
                value={formik.values.description}
              />
            </div>
          </div>

          <div className="flex flex-col">
            {isDesktop && <ExpiryDatePicker />}
            <div>
              <div className="label">
                <span className="label-text text-[1rem] lg:text-xs">Service type *</span>
              </div>
              <Checkbox
                containerClassName="w-14"
                onChange={(e) => formik.setFieldValue("serviceType", e.target.checked ? ["all"] : [])}
                label="All"
                checked={formik.values.serviceType.includes("all")}
              />
              {serviceTypes.map((service) => (
                <Checkbox
                  containerClassName="w-32"
                  key={service.value}
                  onChange={(e) => handleServiceTypeChange(service, e.target.checked)}
                  label={service.label}
                  checked={formik.values.serviceType.includes(service.value)}
                />
              ))}
              {formik.touched.serviceType && formik.errors.serviceType && (
                <div className="label">
                  <span className="label-text-alt text-red-600">{formik.errors.serviceType}</span>
                </div>
              )}
            </div>
            <div className="-space-y-1">
              <Toggle
                labelContainerClassName="flex justify-start gap-4"
                checked={formik.values.isNotifyUser === "true"}
                name="isNotifyUser"
                onChange={(e) => formik.setFieldValue("isNotifyUser", e.target.checked.toString())}
                isInvalid={Boolean(formik.touched.isNotifyUser && formik.errors.isNotifyUser)}
                error={formik.errors.isNotifyUser}
                label="Notify user"
              />
              <Toggle
                labelContainerClassName="flex justify-start gap-[1.32rem]"
                checked={formik.values.isSingleUse === "true"}
                name="isSingleUse"
                onChange={(e) => formik.setFieldValue("isSingleUse", e.target.checked.toString())}
                isInvalid={Boolean(formik.touched.isSingleUse && formik.errors.isSingleUse)}
                error={formik.errors.isSingleUse}
                label="Single use"
              />
            </div>
          </div>

          <div className="col-start-1 col-end-3 flex justify-center mt-3">
            <Button className="w-full md:w-32 min-h-0 h-10" isLoading={formik.isSubmitting} type="submit">
              Add
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddPromoCodeModal;